import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';
import AnimatedLogo from '@/components/AnimatedLogo';
import InstallPWA from '@/components/InstallPWA';
import { APP_VERSION, LAST_UPDATED } from '@/utils/version';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegistering) {
        await register(username, pin);
        toast.success('Registration successful! Please login, bro.');
        setIsRegistering(false);
        setPin('');
      } else {
        const user = await login(username, pin);
        toast.success(`Welcome back, ${user.username}!`);
        navigate(user.is_admin ? '/admin' : '/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Login Section - FIRST */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <AnimatedLogo size={window.innerWidth < 640 ? 96 : 112} />
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-center mb-4 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Bro Tracker
          </h1>
          <p className="text-center text-gray-600 mb-10 text-xl sm:text-2xl font-normal">
            Track your time efficiently, bro
          </p>

          {/* Login Form */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full h-12 text-base rounded-xl border-gray-300 focus:border-primary focus:ring-primary"
                  placeholder="Enter your username"
                />
              </div>

              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                  PIN
                </label>
                <Input
                  id="pin"
                  data-testid="login-pin-input"
                  type="password"
                  inputMode="numeric"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  required
                  className="w-full h-12 text-base rounded-xl border-gray-300 focus:border-primary focus:ring-primary"
                  style={{ fontFamily: 'var(--font-mono)' }}
                  placeholder="Enter your PIN"
                />
              </div>

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold rounded-xl"
              >
                {loading ? 'Hold up, bro...' : (isRegistering ? 'Sign me up, bro!' : 'Let\'s do this, bro')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setIsRegistering(!isRegistering)}
                className="text-sm text-primary hover:underline font-medium"
                data-testid="toggle-register-button"
              >
                {isRegistering ? 'Wait, I\'m already in! Login here, bro' : 'First time? Get in here, bro'}
              </button>
            </div>
          </div>

          {/* Scroll hint */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500 mb-2">Want to know more?</p>
            <ChevronDown className="w-6 h-6 text-gray-400 mx-auto animate-bounce" />
          </div>
        </div>
      </div>

      {/* About Section - BELOW */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Why Bro Tracker */}
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-8 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Why Track Time, Bro?
            </h2>
            <p className="text-xl sm:text-2xl text-gray-700 leading-relaxed mb-6 font-normal">
              Real talk: You work hard, bro. But do you actually know where your 8 hours go? 
              Most people don't. That's a problem.
            </p>
            <p className="text-xl sm:text-2xl text-gray-700 leading-relaxed font-normal">
              Bro Tracker hits you up every 15 minutes. Quick check-in. Log what you did. 
              That's it. No judgment, no micromanaging, just accountability between bros. 
              Because when you track it, you own it.
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-3">⏰</div>
              <h3 className="font-bold text-xl mb-2">Every 15, Bro</h3>
              <p className="text-gray-600">Not too pushy, not too chill. Just right to keep you honest without killing your flow.</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-3">📊</div>
              <h3 className="font-bold text-xl mb-2">Real Data, Bro</h3>
              <p className="text-gray-600">Your boss sees exactly what you crushed. No more "I think I worked on that..." Prove it.</p>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-3">🤝</div>
              <h3 className="font-bold text-xl mb-2">Team Vibes</h3>
              <p className="text-gray-600">Everyone tracks. Everyone improves. Build trust, boost performance, earn respect.</p>
            </div>
          </div>

          {/* The Philosophy of Bro */}
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-8 sm:p-12 rounded-3xl border-2 border-primary/20 shadow-xl">
            <h2 className="text-4xl sm:text-5xl font-bold mb-8 text-center tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              What "Bro" Really Means
            </h2>
            
            <div className="space-y-6 text-gray-800">
              <p className="text-xl leading-relaxed font-normal">
                <strong>Listen up, bro.</strong> This word isn't just some throwaway slang. It goes deep. 
                Way back to the 1960s California surf scene – dudes riding massive waves, trusting each other 
                with their lives. When you're out there in the ocean, "bro" meant <em>"I got your back, always."</em>
              </p>
              
              <p className="text-xl leading-relaxed font-normal">
                It's short for "brother," but not the kind you're born with. It's the kind you <em>choose</em>. 
                The homie who shows up when things get rough. The teammate who celebrates your wins. 
                The real one who tells you the truth even when it's uncomfortable.
              </p>
              
              <p className="text-xl leading-relaxed bg-white/60 p-6 rounded-xl border-l-4 border-primary font-normal">
                <strong>In the workplace?</strong> "Bro" breaks down that fake corporate wall. No more "Dear Sir/Madam" 
                nonsense. No pretending everything's perfect when it's not. Just straight-up respect between people 
                who are in the trenches together, getting it done.
              </p>
              
              <p className="text-xl leading-relaxed font-normal">
                When you're doing field service – repairs, support calls, on-site fixes, traveling between jobs – 
                that's <em>real work</em>. Hands dirty, problems solved, customers happy. But management sitting in 
                their office? They don't see any of that unless you track it.
              </p>
              
              <p className="text-xl leading-relaxed font-normal">
                <strong>Bro Tracker gives you the proof.</strong> Not to snitch on you. Not to micromanage. 
                But to show the full picture of what you contribute. To earn the recognition you've been grinding for. 
                To build a culture where transparency isn't scary – it's just bros being real with each other.
              </p>
              
              <div className="text-center mt-10 pt-8 border-t-2 border-primary/30">
                <p className="text-3xl font-bold mb-3 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  Track your time. Own your story. Respect the grind.
                </p>
                <p className="text-2xl text-gray-700 font-normal">
                  That's what being a bro is all about. 🤝
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-xl sm:text-2xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ready to stop guessing and start proving?
            </p>
            <Button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              size="lg"
              className="text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl"
            >
              Get Started Now, Bro 🚀
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-8 text-center">
        <p className="text-sm opacity-80 mb-2">Bro Tracker • Built by bros, for bros • Track hard, work harder 💯</p>
        <p className="text-xs opacity-60">Version {APP_VERSION} • Last Updated: {LAST_UPDATED}</p>
      </div>

      {/* PWA Install Prompt */}
      <InstallPWA />
    </div>
  );
};

export default LoginPage;
